// Public pages for the ChatGPT app listing: /connect (what the app does and
// how to add it), /privacy and /terms (the policy URLs advertised in the
// OAuth discovery metadata — backend/api/oauthServer.js). No app shell;
// styled with the consent page's tokens (ConnectAuthorize.css).
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Markdown from '../components/Markdown';
import { fetchAgreements } from '../data/agreements';
import SECURITY_MD from '../content/security';
import './ConnectAuthorize.css';

const API = (process.env.REACT_APP_UPLOAD_API || '').replace(/\/+$/, '');
const CONTACT = process.env.REACT_APP_SUPPORT_EMAIL || 'support@fast.legal';

function Shell({ title, children }) {
  return (
    <div className="cx-wrap">
      <div className="cx-card cx-card--wide">
        <div className="cx-brand">
          <Link to="/" className="cx-logo">fast.legal</Link>
          <span>·</span>
          <Link to="/connect" className="cx-link-text">ChatGPT app</Link>
          <Link to="/privacy" className="cx-link-text">Privacy</Link>
          <Link to="/terms" className="cx-link-text">Terms</Link>
        </div>
        <h1 className="cx-title">{title}</h1>
        {children}
      </div>
    </div>
  );
}

export function ConnectInfo() {
  return (
    <Shell title="fast.legal for ChatGPT">
      <p className="cx-muted">
        Run your deal workspace from a chat. fast.legal connects to ChatGPT as an app
        (Model Context Protocol) so you can read, draft, negotiate and export against your
        real projects — the same documents, Deal Points Register, versions, memos, workbooks
        and signatures you use in the app.
      </p>
      <h2 className="cx-h2">What you can do</h2>
      <ul className="cx-list">
        <li><strong>Read</strong> — list projects, open documents and clauses, compare versions, review the Deal Points Register, ask the project brain.</li>
        <li><strong>Draft</strong> — the drafting agent proposes edits you apply, authors new documents, derives filings from agreements, drafts memos and emails.</li>
        <li><strong>Negotiate</strong> — record positions, reconcile counterparty correspondence, check conformance, render the Stance-by-Party summary PDF.</li>
        <li><strong>Deliver</strong> — export clean or redlined Word/PDF, package exports, issue certificates, send signature envelopes.</li>
      </ul>
      <h2 className="cx-h2">Add it to ChatGPT</h2>
      <ol className="cx-list">
        <li>In ChatGPT open <em>Settings → Apps &amp; Connectors</em> and add a custom app (developer mode) or find <em>fast.legal</em> in the directory.</li>
        <li>Server URL: <code className="cx-code">{API ? `${API}/mcp` : 'https://<api>/mcp'}</code></li>
        <li>Sign in with your fast.legal account and choose the scopes to grant (read, write). You can revoke access any time under <em>Team → Connected apps</em>.</li>
      </ol>
      <p className="cx-muted">
        Every change made through ChatGPT is attributed to you ("via ChatGPT") in the project
        audit trail. Binary file uploads (.docx/.pdf/.xlsx) are faster in the app; the ChatGPT
        app accepts pasted text, public URLs and pre-authorized uploads.
      </p>
      <p className="cx-muted">Questions: <a href={`mailto:${CONTACT}`}>{CONTACT}</a></p>
    </Shell>
  );
}

// /privacy and /terms render the active clickwrap text (legal/*.md →
// agreements/{kind}-v{n} via scripts/seed-agreements.mjs) — the same words
// a person agrees to at signup, so the public page and the gate never drift.
function Agreement({ kind, title }) {
  const [state, setState] = useState({ doc: undefined, error: '' });
  useEffect(() => {
    let alive = true;
    fetchAgreements(kind)
      .then((list) => { if (alive) setState({ doc: list.find((d) => d.kind === kind) || null, error: '' }); })
      .catch((err) => { if (alive) setState({ doc: null, error: err.message }); });
    return () => { alive = false; };
  }, [kind]);
  const { doc, error } = state;
  return (
    <Shell title={doc?.title || title}>
      {doc === undefined && <div className="cs-skeleton" style={{ height: 320 }} />}
      {doc && <Markdown text={doc.body.replace(/^#\s+.+\n/, '')} className="md cx-md" />}
      {doc === null && (
        <p className="cx-muted">
          This document could not be loaded right now{error ? ` (${error})` : ''}. Write to{' '}
          <a href={`mailto:${CONTACT}`}>{CONTACT}</a> for a copy.
        </p>
      )}
    </Shell>
  );
}

export const Privacy = () => <Agreement kind="privacy" title="Privacy policy" />;
export const Terms = () => <Agreement kind="tos" title="Terms of service" />;
export const Dpa = () => <Agreement kind="dpa" title="Data processing addendum" />;

// /security — the posture statement (not an agreement; text in
// content/security.js, kept in step with legal/dpa.md §2–3).
export const Security = () => (
  <Shell title="Security">
    <Markdown text={SECURITY_MD} className="md cx-md" />
  </Shell>
);
